<template>
  <b-card no-body>
    <b-card-body>
      <admin-special-offer-filter
        :values="filterValues"
        :filter-submitting="isLoading"
        :providers="providers"
        @filter="setFilter"
      />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'consignmentId'">
            {{ props.row.consignment.id || '-' }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge pill class="text-white" :variant="getOfferStatusColor(props.row.status)">
              {{ getOfferStatusName(props.row.status) }}
            </b-badge>
          </span>
          <div v-else-if="props.column.field === 'provider'">
            <div class="d-flex flex-row align-items-center">
              <img :src="getProviderLogo(props.row.provider)" alt="Logo" class="provider-logo" />
              <span class="ml-1">{{ props.row.provider.name }}</span>
            </div>
          </div>
          <span v-else-if="props.column.field === 'totalCost'">
            {{ props.row.totalCost }}
          </span>
          <span v-else-if="props.column.field === 'estimatedReturnCost'">
            {{ props.row.estimatedReturnCost }}
          </span>
          <div v-else-if="props.column.field === 'createdAt'">
            <div>
              <span>
                {{ props.row.createdAt || '-' }}
              </span>
            </div>
            <div v-if="props.row.updatedAt && props.row.updatedAt !== props.row.createdAt">
              <span> ({{ `${t('Updated At')}: ${props.row.updatedAt}` }}) </span>
            </div>
          </div>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <template />
              <template />

              <b-button
                v-if="checkHasRole(CUSTOMER_ROLES)"
                :id="`approve-button-${props.row.id}`"
                variant="success"
                type="button"
                size="sm"
                @click="approveSpecialOffer(props.row.id)"
              >
                <feather-icon icon="CheckIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`approve-button-${props.row.id}`"
                  :title="t('Approve Special Offer')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>

              <b-button
                v-if="checkHasRole(CUSTOMER_ROLES)"
                :id="`reject-customer-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="rejectSpecialOffer(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`reject-customer-button-${props.row.id}`"
                  :title="t('Reject Special Offer')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BBadge, BCard, BCardBody, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import AdminSpecialOfferFilter from '@/views/pages/special-offer/customer/SpecialOfferFilter.vue';
import useService from '@/auth/service/useService';
import store from '@/store';
import { CUSTOMER_ROLES } from '@/constants/roles';
import { checkHasRole } from '@core/utils/roleAccess';
import { getProviderLogo } from '@/libs/providerHelper';

export default {
  components: {
    VueGoodTable,
    BBadge,
    BCard,
    BCardBody,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    AdminSpecialOfferFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        consignment: '',
        status: '',
        provider: ''
      },
      defaultFilterValues: {
        consignment: '',
        status: '',
        provider: ''
      },
      providers: [],
      offerStatusList: []
    };
  },
  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Consignment ID'),
          field: 'consignmentId'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Provider'),
          field: 'provider'
        },
        {
          label: this.$t('Total Cost'),
          field: 'totalCost'
        },
        {
          label: this.$t('Estimated Return Cost'),
          field: 'estimatedReturnCost'
        },
        {
          label: this.$t('Created At'),
          field: 'createdAt'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    if (checkHasRole(CUSTOMER_ROLES)) {
      this.providers = await this.getProviders({ page: 1, limit: 1000 });
    }

    this.offerStatusList = await this.getOfferStatus();
    this.loadItems();
  },
  methods: {
    checkHasRole,
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    // load items is what brings back the rows from server
    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      this.isLoading = true;
      useService
        .getAllOffers(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    },
    getOfferStatusName(id) {
      return this.offerStatusList.find(x => parseInt(x.value) === parseInt(id)).info || '-';
    },
    getOfferStatusColor(status) {
      let color = 'secondary';

      if (status === 1) {
        color = 'primary';
      } else if (status === 2) {
        color = 'success';
      } else if ([3, 4, 5].includes(status)) {
        color = 'danger';
      }

      return color;
    },
    approveSpecialOffer(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Special Offer Approve Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.approveSpecialOfferRequest(id)
            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Special Offer Was Approved')}`
              );

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },
    rejectSpecialOffer(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Special Offer Delete Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.rejectSpecialOfferRequest(id)
            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Special Offer Was Rejected')}`
              );

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },
    async rejectSpecialOfferRequest(id) {
      return useService.rejectOffer(id);
    },
    async approveSpecialOfferRequest(id) {
      return useService.approveOffer(id);
    },
    getProviderLogo
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table';
</style>
